import { Box } from '@chakra-ui/react';
import cn from 'classnames';
import React from 'react';
import { useRecoilState } from 'recoil';

import { PAGE_SIZE } from '@/constants/used-cars/used-cars.constants';
import { useIndexSearch } from '@/hooks/filters/index-search.hook';
import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';
import { useVehicles } from '@/hooks/used-cars/vehicles.hook';
import { getVehiclesWithFilters } from '@/services/used-cars/used-cars.service';
import { isSearchOpenState } from '@/states/used-cars/filter-query.state';
import {
  CarSearchMeta,
  SortOption,
  UserCarsFilterQuery,
  Vehicle,
  VehicleType,
  ViewModes,
} from '@/types/used-cars/used-cars.types';

import { BlockWrapper } from '../../block-wrapper/block-wrapper';
import { CarTile } from '../../car-tile/car-tile';
import { Pagination } from '../../pagination/pagination';
import { LocationSelect } from '../location-select/location-select';
import { SortSelect } from '../sort-select/sort-select';

import styles from './search-results.module.scss';

export const SearchResults: React.FC<{
  hideLocation?: boolean;
  dealershipId?: string;
  type?: VehicleType | null;
  additionalFilters?: UserCarsFilterQuery | null;
  initialVehicles?: Vehicle[];
  initialVehiclesMeta?: CarSearchMeta;
}> = ({
  hideLocation,
  dealershipId,
  type,
  additionalFilters = {},
  initialVehicles,
  initialVehiclesMeta,
}) => {
  const {
    setVehicles,
    setVehiclesMeta,
    vehiclesMeta,
    vehicles,
    vehiclesView,
    initial,
  } = useVehicles();

  const { filtersQuery } = useFiltersQuery();
  const isListView = vehiclesView === ViewModes.list;
  const [isSearchOpen] = useRecoilState(isSearchOpenState);
  const { setIndexPage } = useIndexSearch();

  const allVehiclesMeta = initial ? initialVehiclesMeta : vehiclesMeta;
  const isIndexSearch = allVehiclesMeta?.isIndexSearch;
  const allVehicles = initial ? initialVehicles : vehicles;

  async function onPageChange(page: number) {
    if (isIndexSearch) {
      // set the requested page in the recoil state so the pagination can pick it up and do an index refinement
      setIndexPage(page);
    } else {
      // Soft change the URL without reloading:
      const url = new URL(window.location.href);

      if (page === 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', page.toString());
      }
      window.history.pushState({}, '', url);

      const meta = { ...vehiclesMeta, currentPage: page };

      setVehiclesMeta(meta);
      const { vehicles: newVehicles } = await getVehiclesWithFilters(
        { ...filtersQuery, ...additionalFilters },
        meta,
        dealershipId,
        type,
      );

      setVehicles(newVehicles);
    }

    window.scrollTo(0, 0);
  }

  async function onSortChange(sort: SortOption) {
    const meta = { ...vehiclesMeta, sort, currentPage: 1 };

    setVehiclesMeta(meta);
    const { vehicles: newVehicles } = await getVehiclesWithFilters(
      { ...filtersQuery, ...additionalFilters },
      meta,
      dealershipId,
      type,
    );

    setVehicles(newVehicles);
  }

  function mapVehiclesTiles(vehicle: Vehicle) {
    const Tile = (
      <div
        className={cn(styles.resultsItem, {
          [styles.carTileList as string]: isListView,
        })}
        key={vehicle.id}
      >
        <CarTile special={false} details={vehicle} />
      </div>
    );

    return isListView ? (
      Tile
    ) : (
      <div
        key={vehicle.id}
        className={cn(styles.carTileTextColor, styles.carTile)}
      >
        {Tile}
      </div>
    );
  }

  return (
    <div className={styles.bodyWrapper}>
      {isSearchOpen && <div className={styles.bodyWrapperBlur}></div>}
      <BlockWrapper>
        <div className={styles.resultsHeader}>
          <div
            className={styles.resultsCount}
            data-cy="used-cars-results-count"
          >
            {allVehiclesMeta?.filterCount} cars found
          </div>
          <div className={styles.resultsActions}>
            {!hideLocation && (
              <Box className={styles.mobHide}>
                <LocationSelect />
              </Box>
            )}
            {/* If we've searched the index, results will come sorted according to
            best match, so we hide the sort select */}
            {!isIndexSearch && (
              <SortSelect
                onSortChange={onSortChange}
                overlayClassName={styles.sortByMenu}
              />
            )}
          </div>
        </div>
        <div className={styles.resultsMain}>
          {allVehicles?.map(mapVehiclesTiles)}
        </div>
        {(allVehiclesMeta?.filterCount as number) > PAGE_SIZE && (
          <Pagination
            onPageChange={onPageChange}
            itemsMeta={vehiclesMeta}
            pageSize={PAGE_SIZE}
          />
        )}
      </BlockWrapper>
    </div>
  );
};

export default SearchResults;
